import { FileUploader } from "./FileUploader";
import { Box, Button, Flex, Grid, Text, TextArea } from "@radix-ui/themes";
import styles from "../styles/Conversation.module.scss";
import { AttachmentPreview } from "./AttachmentPreview";
import classNames from "classnames";
import { Dispatch, SetStateAction, useRef } from "react";
import { IPendingMessageAttachment } from "./Conversation";
import { useStores } from "../../../stores/useStores";
import { MessagingListRulesQuery } from "../../modals/Messaging/MessagingListRules";
import { IChannel } from "./ChannelsList";

export function MessageInput(props: {
  inputText: string;
  attachments: IPendingMessageAttachment[];
  setAttachments: Dispatch<SetStateAction<IPendingMessageAttachment[]>>;
  setInputText: Dispatch<SetStateAction<string>>;
  handleSend: () => void;
  recipients: IUser[];
  recipientRules: MessagingListRulesQuery[];
  currentChannel: IChannel | undefined;
  disableReplies: boolean;
  canSendToBroadcastList: boolean;
}) {
  const { userStore } = useStores();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  function addAttachment(attachment: IPendingMessageAttachment) {
    props.setAttachments((prev) => [...prev, attachment]);
  }

  function deleteAttachment(url: IPendingMessageAttachment["url"]) {
    props.setAttachments((prev) => prev.filter((a) => a.url !== url));
  }

  function handleTextChange(e) {
    props.setInputText(e.target.value);
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      const newScrollHeight = Math.min(textAreaRef.current.scrollHeight, 200);
      textAreaRef.current.style.height = newScrollHeight + "px";
    }
  }

  const replyClassName = classNames({
    [styles.disabledReplies]: props.disableReplies,
  });

  const isDisabled =
    !props.inputText ||
    // no recipients and not sending to an existing list
    (props.recipients.length === 0 &&
      props.recipientRules.length === 0 &&
      !props.currentChannel?.id) ||
    // current user is only recipient and not sending to an existing list
    (props.recipients.length === 1 &&
      props.recipients[0].id === userStore.id &&
      props.recipientRules.length === 0);

  return (
    <Grid
      columns={"2"}
      rows={"3"}
      gapX={"3"}
      align={"center"}
      className={replyClassName}
      style={{ gridTemplateColumns: "auto 1fr", gridTemplateRows: "auto 1fr" }}
    >
      <FileUploader addAttachment={addAttachment} />
      <Flex align={"center"} gapX={"2"} asChild>
        <form action={props.handleSend}>
          <Box style={{ flexGrow: 1 }}>
            <TextArea
              ref={textAreaRef}
              onChange={handleTextChange}
              style={{ minHeight: "unset" }}
              size={"3"}
              rows={1}
              wrap={"soft"}
              placeholder={"Type message here"}
              value={props.inputText}
            />
          </Box>
          <Button variant={"soft"} disabled={isDisabled}>
            Send
          </Button>
        </form>
      </Flex>
      {props.canSendToBroadcastList && (
        <Box gridColumnStart={"2"}>
          <Text weight={"bold"}>This list does not allow replies.</Text>
        </Box>
      )}
      {props.attachments.length > 0 && (
        <Box mt={"2"} gridColumnStart={"2"}>
          <Flex gap={"2"} wrap={"wrap"}>
            {props.attachments.map((attachment) => (
              <AttachmentPreview
                key={attachment.url}
                attachment={attachment}
                deleteAttachment={deleteAttachment}
              />
            ))}
          </Flex>
        </Box>
      )}
    </Grid>
  );
}
